import { filter, find, isEmpty, map, some, sumBy } from 'es-toolkit/compat';
import pLimit from 'p-limit';

import {
  FORMATTED_CELL_TABLE_READ_CONCURRENCY,
  FormattedCellTableEntry,
  loadFormattedCellTableEntries,
} from './formattedCellTables';
import {
  CellTableRunDiscoveryFailure,
  CellTableRunScope,
  CellTableRunsDiscoveryProgress,
  discoverCellTableArtifactFilesForRuns,
} from './workflowCellTableDiscovery';

const RUN_DISCOVERY_CONCURRENCY = 5;

export interface KeyedCellTableRunScope extends CellTableRunScope {
  // Names the study, results source and run, so the same run is never read twice.
  key: string;
}

interface CachedRun {
  scope: KeyedCellTableRunScope;
  entries: FormattedCellTableEntry[];
  complete: boolean;
  collapsedDuplicates: number;
}

// What one dialog has read so far, per run. A run is read once; picking it again, or another run next to it,
// does not read it again.
export interface CellTableRunCache {
  runs: Map<string, CachedRun>;
  refreshes: Map<string, Promise<void>>;
}

export const createCellTableRunCache = (): CellTableRunCache => ({ runs: new Map(), refreshes: new Map() });

export interface LoadedCellTableRuns {
  entries: FormattedCellTableEntry[];
  failures: CellTableRunDiscoveryFailure[];
  emptyRunIds: string[];
  // Files that shared a URL with another listed file and are listed only once.
  collapsedDuplicates: number;
  // Tables of runs read in this call, which the user has not had the chance to select or deselect yet.
  newEntryIds: string[];
}

export interface CellTableRunsLoadProgress extends LoadedCellTableRuns {
  pendingFiles: number;
  searchingRuns: number;
}

// The tables of the scopes that were already read, for showing while the others load.
export const getCachedCellTableRuns = (cache: CellTableRunCache, scopes: KeyedCellTableRunScope[]) => {
  const cached = filter(
    map(scopes, (scope) => ({ scope, run: cache.runs.get(scope.key) })),
    ({ run }) => Boolean(run)
  );
  return {
    hasCachedRun: !isEmpty(cached),
    entries: cached.flatMap(({ run }) => run?.entries ?? []),
    collapsedDuplicates: sumBy(cached, ({ run }) => run?.collapsedDuplicates ?? 0),
    emptyRunIds: cached.flatMap(({ scope, run }) =>
      scope.runId && run?.complete && isEmpty(run.entries) ? [scope.runId] : []
    ),
  };
};

export const hasUncachedRuns = (cache: CellTableRunCache, scopes: KeyedCellTableRunScope[]) =>
  some(scopes, ({ key }) => !cache.runs.get(key)?.complete);

// Completed tables stay usable while the rest load. Incomplete runs retry only their unresolved/error tables.
export const loadCellTableRuns = async ({
  studyId,
  scopes,
  cache,
  signal,
  onDiscoveryProgress,
  onReadProgress,
  onUpdate,
}: {
  studyId: string;
  scopes: KeyedCellTableRunScope[];
  cache: CellTableRunCache;
  signal: AbortSignal;
  onDiscoveryProgress?: (progress: CellTableRunsDiscoveryProgress) => void;
  onReadProgress?: (progress: { readFiles: number; totalFiles: number }) => void;
  onUpdate?: (progress: CellTableRunsLoadProgress) => void;
}): Promise<LoadedCellTableRuns> => {
  const missing = filter(scopes, ({ key }) => !cache.runs.get(key)?.complete);
  const failuresByRun = new Map<string, CellTableRunDiscoveryFailure[]>();
  const pendingByRun = new Map<string, number>();
  const discover = pLimit(RUN_DISCOVERY_CONCURRENCY);
  const read = pLimit(FORMATTED_CELL_TABLE_READ_CONCURRENCY);
  const previousReadyIds = new Set(
    map(filter(getCachedCellTableRuns(cache, scopes).entries, { status: 'ready' }), (entry) => entry.file.id)
  );
  let searchingRuns = missing.length;
  const inventory = (): LoadedCellTableRuns => {
    const entries = scopes.flatMap(({ key }) => cache.runs.get(key)?.entries ?? []);
    return {
      entries,
      failures: scopes.flatMap(({ key }) => failuresByRun.get(key) ?? []),
      collapsedDuplicates: sumBy(scopes, ({ key }) => cache.runs.get(key)?.collapsedDuplicates ?? 0),
      emptyRunIds: scopes.flatMap(({ key, runId }) => {
        const run = cache.runs.get(key);
        return runId && run?.complete && isEmpty(run.entries) ? [runId] : [];
      }),
      newEntryIds: map(
        filter(entries, (entry) => entry.status === 'ready' && !previousReadyIds.has(entry.file.id)),
        (entry) => entry.file.id
      ),
    };
  };
  const report = () =>
    onUpdate?.({
      ...inventory(),
      pendingFiles: [...pendingByRun.values()].reduce((total, count) => total + count, 0),
      searchingRuns,
    });
  report();
  await Promise.all(
    missing.map(async (scope, index) => {
      const discovery = await discover(() =>
        discoverCellTableArtifactFilesForRuns({
          scopes: [scope],
          signal,
          onProgress: (progress) => onDiscoveryProgress?.({ ...progress, runIndex: index, runCount: missing.length }),
        })
      );
      searchingRuns -= 1;
      if (!isEmpty(discovery.failures)) {
        failuresByRun.set(scope.key, discovery.failures);
        report();
        return;
      }
      const { files } = discovery;
      const retained = new Map(
        map(
          filter(cache.runs.get(scope.key)?.entries ?? [], (entry) => entry.status !== 'error'),
          (entry) => [entry.file.id, entry]
        )
      );
      const run: CachedRun = {
        scope,
        entries: files.flatMap((file) => {
          const entry = retained.get(file.id);
          return entry ? [{ ...entry, file }] : [];
        }),
        complete: false,
        collapsedDuplicates: discovery.collapsedDuplicates,
      };
      cache.runs.set(scope.key, run);
      const unread = filter(files, (file) => !retained.has(file.id));
      let pendingFiles = unread.length;
      pendingByRun.set(scope.key, pendingFiles);
      report();
      await loadFormattedCellTableEntries({
        studyId,
        files: unread,
        signal,
        limit: read,
        onProgress: onReadProgress,
        onEntry: (entry) => {
          const byId = new Map(map([...run.entries, entry], (listed) => [listed.file.id, listed]));
          run.entries = files.flatMap((file) => {
            const listed = byId.get(file.id);
            return listed ? [listed] : [];
          });
          pendingFiles -= 1;
          pendingByRun.set(scope.key, pendingFiles);
          report();
        },
      });
      run.complete = !some(run.entries, { status: 'error' });
      report();
    })
  );
  return inventory();
};

// The id ends with the file's position in the discovery, which can change when the run is read again.
const stableId = (id: string) => id.replace(/:\d+$/, '');

const refreshRun = async (run: CachedRun, signal: AbortSignal) => {
  const { files, failures } = await discoverCellTableArtifactFilesForRuns({ scopes: [run.scope], signal });
  if (!isEmpty(failures)) throw new Error(failures[0].message);
  const freshById = new Map(map(files, (file) => [stableId(file.id), file]));
  // Only the links change: ids, selection and the reviewed column mapping stay as the user saw them.
  run.entries = map(run.entries, (entry) => {
    const fresh = freshById.get(stableId(entry.file.id));
    return fresh ? { ...entry, file: { ...fresh, id: entry.file.id } } : entry;
  });
};

// Asks again for the run's file links, for a table whose cached link stopped working. Resolves with the table as it
// is now cached.
export const refreshCellTableEntryLinks = async ({
  cache,
  entry,
  signal,
}: {
  cache: CellTableRunCache;
  entry: FormattedCellTableEntry;
  signal: AbortSignal;
}): Promise<FormattedCellTableEntry> => {
  const key = find([...cache.runs.keys()], (runKey) =>
    some(cache.runs.get(runKey)?.entries, (cached) => cached.file.id === entry.file.id)
  );
  const run = key ? cache.runs.get(key) : undefined;
  if (!key || !run) return entry;
  let refresh = cache.refreshes.get(key);
  if (!refresh) {
    refresh = refreshRun(run, signal).finally(() => cache.refreshes.delete(key));
    cache.refreshes.set(key, refresh);
  }
  await refresh;
  return find(cache.runs.get(key)?.entries, (cached) => cached.file.id === entry.file.id) ?? entry;
};
